import debounce from "lodash/debounce";
import scrollTo from "../utils/scrollTo";
import { Easings, linearScale, WebGLAssets } from "@scrolly/renderer";
import { computeScrollSmoothTimer } from "../utils/scrollSmoothTimer";
import { navZiel } from "../utils/navZiel";
import { gleichmaessig, radAbsicht } from "../utils/punktSprung";
import { positionsSkala } from "../utils/positionsSkala";
import { rahmenGleichmaessigSkaliert } from "../utils/kartenSkala";
import PlayablePresentation from "./PlayablePresentation";
import {
    calculateSlidePosition,
    responsiveMultiplier,
    slidePositionBreakpoints,
} from "./calculateSlidePosition";

const IS_HTML_MODE = typeof document === "undefined";

/**
 * Callback invoked by the renderer when a user-observable event happens
 * (currently: `"storyNavigation"` fires when the scroll crosses a slide
 * boundary). Consumers plug in their own tracking — the renderer stays
 * analytics-agnostic.
 *
 * Payload shape per event name:
 * - `"storyNavigation"`: `{ slide: number; totalSlides: number; type: "scroll" }`
 */
export type VisualStoryEventHandler = (name: string, payload: unknown) => void;

const SCROLL_SPEED = 1400;
// Sprünge über die Kapitel-Punkte: bis so viele Slides weit läuft der Scrolly
// im Timeline-Tempo hin, weiter weg wird geschnitten (springeZuSlide)
const SPRUNG_NAH = 2;
// we use the offset to combat the fact, that we need to scroll the "dead zone"
const SCROLL_TIME_OFFSET = 400;
// Intro-Tween: nach Cover-Start scrollt der Viewer programmatisch zu
// Slide-1.textCenter, damit der User die erste Animation einmal abspielen
// sieht und versteht: „aha, das ist scroll-getrieben". Länger als SCROLL_SPEED,
// damit Layer-Übergänge wahrnehmbar sind.
// Iteration 2026-05-28: 2200ms (gar nicht spürbar wegen iOS-Cache) → 4000ms
// (debug-bump, klar sichtbar) → 3000ms („etwas schneller wäre besser") →
// 2000ms (final, knackig).
const INTRO_DURATION = 2000;
/** Sentence-Bloom intro: auto-scroll only until this many quote words are visible. */
const SENTENCE_BLOOM_INTRO_VISIBLE_WORDS = 3;
// when a user makes a click in `FAST_CLICK` ms again, we are in fast mode
const FAST_CLICK = 800;
// when the user is `FAST_SCROLL_SLIDES_AHEAD` slides head with scrolling, we are in fast mod
const FAST_SCROLL_SLIDES_AHEAD = 1;
// after `SLOW_SCROLL_TIMEOUT`ms we show the animations again
const SLOW_SCROLL_TIMEOUT = 1200;
// Editor: so viele Sync-Frames nach einer Neuvermessung (Formatwechsel, Zoom)
// gilt der Playhead als Wahrheit und die Vorschau scrollt zu ihm — statt dass
// die verschobene Scroll-Position den Playhead verstellt (s. editorRealignFrames).
const EDITOR_REALIGN_FRAMES = 6;

interface SlidePosition {
    top: number;
    animateAreaFrom: number;
    textCenter: number;
    height: number;
}

/**
 * Hoehe des GROSSEN Viewports (CSS `100lvh`) = Bildschirm bei eingeklappter
 * Browser-Leiste. Seiten-Scroll-Test (28.09.2026): Die Buehne steht im
 * window-Modus fixed auf 100lvh. Misst der Store dieselbe Hoehe, sind Canvas,
 * Kamera und Slide-Hoehen deckungsgleich und bleiben es, wenn iOS die Leiste
 * ein- oder ausklappt (innerHeight/visualViewport aendern sich dabei, lvh nicht).
 */
function grosseViewportHoehe(): number {
    if (typeof document === "undefined") return 0;
    const probe = document.createElement("div");
    probe.style.cssText =
        "position:absolute;top:0;left:0;width:1px;height:100lvh;visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const h = probe.getBoundingClientRect().height;
    probe.remove();
    return h > 0 ? h : window.innerHeight;
}

// Rast-Strecke boxloser Slides als Anteil der Viewport-Höhe: der Zeitgeber
// steht dieses Stück am Slide-Ende still, bevor der nächste übernimmt — das
// kurze „Einrasten", das Slides mit HTML-Box durch ihre Lesezone ohnehin haben
// (dort abhängig von der Boxhöhe, typisch ⅓ bis ½ Viewport).
const RAST_OHNE_BOX = 0.2;

export class VisualStoryStore {
    post: PlayablePresentation | undefined = undefined;

    scrollTop = 0;
    // Editor-Text-Sync: letzter gesehener Master-Timer (slidePlayer.timer). Dient
    // dazu, timeline-/play-getriebene Timer-Änderungen (→ Scroll nachziehen) von
    // User-Scroll (→ Master treiben) zu unterscheiden. Sentinel NaN erzwingt keinen
    // Sync beim allerersten Frame (masterMoved wäre sonst true).
    private lastEditorMasterTimer = NaN;
    // Editor: nach einer Neuvermessung der Slide-Positionen (Formatwechsel
    // AUTO→1:1, Interface-Zoom, Fenster) muss die Vorschau zum Playhead
    // scrollen — NICHT der Playhead aus der (jetzt anders bedeutenden)
    // Scroll-Position neu gesetzt werden. Jans Fund 04.09.2026: Cursor auf
    // Slide 3, Ansicht auf 1:1 → Cursor sprang eine halbe Sekunde weiter.
    // Zaehler statt Flag: ein paar Frames Nachlauf, weil Chromes Scroll-
    // Anchoring die Scroll-Position erst NACH der Neuvermessung nachjustiert
    // (Layout → ResizeObserver → Anchoring, alles nach dem rAF-Sync).
    private editorRealignFrames = 0;
    // Editor: Frame-Groesse des letzten Sync-Frames. Der ResizeObserver laeuft
    // erst NACH den rAF-Callbacks, ein Frame lang saehe der Sync also die
    // neue Scroll-Position mit den ALTEN Slide-Positionen → hier abfangen.
    private lastEditorFrameW = 0;
    private lastEditorFrameH = 0;
    dom: HTMLElement | undefined = undefined;
    viewerFrame: HTMLElement;
    heightFrame: HTMLElement;
    /**
     * Scroll-Source-Polarität (Plan-2 iOS-Fix 2026-06-01). DEFAULT "container"
     * = heutiges Verhalten (Renderer liest viewerFrame.scrollTop, Höhe aus
     * heightFrame). Nur der public/-Immersive-Full-Page-Reader opted auf
     * "window" (window.scrollY + window.innerHeight) — entkoppelt vom
     * dvh/URL-Bar-Hack UND vom verzögerten nested clientHeight (3ce09c7-Pfad).
     */
    scrollSource: "container" | "window" = "container";
    // Gecachte Frame-Höhe (Snapshot pro Breite, s. getFrameDimensions): gated den
    // resize-Recompute beim iOS-URL-Bar-Kollaps, damit die Slides nicht reflowen.
    private cachedFrameHeight: number | null = null;
    private cachedFrameWidth: number | null = null;
    /** Template-Editor-Vorschau (Jans iPhone-Fund 13.09.2026): der Rahmen ist
     *  die Buehnen-Box, kein Fenster mit Adressleiste — ein Ratio-Wechsel bei
     *  gleicher Breite (9:16 → 1:1) aendert NUR die Hoehe, und der Per-Breite-
     *  Cache lieferte weiter die alte Hoehe. Ohne Cache misst jeder Aufruf neu. */
    private ohneHoehenCache = false;
    /**
     * Ist der Rahmen eine BOX in der Seite (Dialog, Kachel, Editor-Buehne) —
     * oder das Fenster (veroeffentlichter Viewer)?
     *
     * Daran haengen ZWEI Regeln, die beide nur dem Fenster gehoeren: der
     * Per-Breite-Hoehen-Cache und der visualViewport-Vorrang. Beide gehen von
     * „Box == Fenster" aus. Bene 21.09.2026: In einer 443-px-Box stand der
     * Vorhang auf 612 px, der START-Knopf lag darunter, und weil vor dem Start
     * nicht gescrollt werden darf, war er nicht erreichbar.
     */
    private get rahmenIstBox(): boolean {
        return this.editorMode || this.ohneHoehenCache;
    }
    stopUpdate = false;
    slidePositions: Array<SlidePosition> = [];
    /* The following offset was hardcoded to be a high number initially.
     * This lets the page always scroll down to a position where the credits
     * should be visible.
     */
    creditsScrollOffset = 1000000000000000;
    innerHeight = 800;
    innerWidth = 320;

    // fast scrolling/clicking attributes
    fastScroll = true;
    lastSlideIndex = -1;
    lastButtonClick: Date = new Date();
    lastSlideTimestamp: number = +new Date();
    fastClick = false;
    makeSlideSlowAgainTimeout: number | undefined;
    stopped = true;
    rafId: number | null = null;

    heightSubscribers: (() => void)[] = [];
    widthSubscribers: (() => void)[] = [];
    beforeStorySubscribers: (() => void)[] = [];
    afterStorySubscribers: (() => void)[] = [];
    cancelScroll? = () => {};
    private isProgrammaticScroll = false;

    /** Ziel-Slide eines laufenden Nav-Knopf-Tweens (null = kein Tween). */
    private knopfZielIndex: number | null = null;
    debouncedUpdate: () => void = debounce(
        () => {
            const changed = this.regenerateBoundsCache();
            if (changed) {
                this.regenerateSlidePositionsCache();
                // initalize again as we might overwrite the textWidth
                setTimeout(() => {
                    this.regenerateSlidePositionsCache();
                }, 10);
            }
        },
        100,
        { trailing: true }
    );

    subscribeToHeightChanges = (callback: () => void) => {
        this.heightSubscribers.push(callback);
        return () => {
            this.heightSubscribers = this.heightSubscribers.filter((sub) => sub !== callback);
        };
    };

    subscribeToWidthChanges = (callback: () => void) => {
        this.widthSubscribers.push(callback);
        return () => {
            this.widthSubscribers = this.widthSubscribers.filter((sub) => sub !== callback);
        };
    };

    subscribeToBeforeStoryChanges = (callback: () => void) => {
        this.beforeStorySubscribers.push(callback);
        return () => {
            this.beforeStorySubscribers = this.beforeStorySubscribers.filter((sub) => sub !== callback);
        };
    };

    subscribeToAfterStoryChanges = (callback: () => void) => {
        this.afterStorySubscribers.push(callback);
        return () => {
            this.afterStorySubscribers = this.afterStorySubscribers.filter((sub) => sub !== callback);
        };
    };

    emitHeightChanges = () => {
        this.heightSubscribers.forEach((sub) => sub());
    };

    emitWidthChanges = () => {
        this.widthSubscribers.forEach((sub) => sub());
    };

    emitBeforeStoryChanges = () => {
        this.beforeStorySubscribers.forEach((sub) => sub());
    };

    emitAfterStoryChanges = () => {
        this.afterStorySubscribers.forEach((sub) => sub());
    };

    heightSnapshot = () => {
        return this.innerHeight;
    };

    widthSnapshot = () => {
        return this.innerWidth;
    };

    afterStorySnapshot = () => {
        return this.afterStory;
    };

    beforeStorySnapshot = () => {
        return this.beforeStory;
    };

    private onEvent?: VisualStoryEventHandler;

    /**
     * Editor-Verhalten PRO INSTANZ statt global (WebGLAssets.editorMode):
     * Im Pro-Editor laufen seit der Bühnen-Vorschau ZWEI Viewer gleichzeitig —
     * der als Editor-Canvas eingebettete (Timer-Umleitung in Jans Playhead,
     * kein Start-Scroll) und der Vorschau-Dialog, der sich wie der LESER
     * verhalten muss. Die globale Flagge sperrte im Dialog den Start und
     * leitete jede Scroll-Bewegung in die Editor-Timeline um („lässt sich
     * nicht starten", Jans Fund 08.08.2026). Vorgabe kommt aus dem
     * proEditor-Prop des ScrollyViewers; ohne Angabe gilt die globale Flagge
     * zum Bauzeitpunkt (bestehende Aufrufer unverändert).
     */
    readonly editorMode: boolean;

    constructor(
        viewerFrame: HTMLElement,
        heightRefFrame: HTMLElement,
        onEvent?: VisualStoryEventHandler,
        scrollSource: "container" | "window" = "container",
        editorMode?: boolean,
        ohneHoehenCache?: boolean,
    ) {
        this.viewerFrame = viewerFrame;
        this.heightFrame = heightRefFrame;
        this.onEvent = onEvent;
        this.scrollSource = scrollSource;
        this.editorMode = editorMode ?? WebGLAssets.editorMode;
        this.ohneHoehenCache = ohneHoehenCache ?? false;
        this.nextSlide = this.nextSlide.bind(this);
        this.previousSlide = this.previousSlide.bind(this);
    }

    /**
     * Scroll-Source-Accessoren (Plan-2). "container" = viewerFrame (heute);
     * "window" = document-scroll (entkoppelt vom dvh/URL-Bar + nested clientHeight).
     */
    getScrollTop(): number {
        return this.scrollSource === "window"
            ? window.scrollY
            : this.viewerFrame.scrollTop;
    }

    getMaxScroll(): number {
        return this.scrollSource === "window"
            ? document.documentElement.scrollHeight - window.innerHeight
            : this.viewerFrame.scrollHeight - this.viewerFrame.clientHeight;
    }

    getScrollContext(): Window | HTMLElement {
        return this.scrollSource === "window" ? window : this.viewerFrame;
    }

    setScrollTopValue(value: number): void {
        if (this.scrollSource === "window") {
            window.scrollTo(0, value);
        } else {
            this.viewerFrame.scrollTop = value;
        }
    }

    /**
     * Frame-Rect für die Slide-Top-Berechnung (initialisePositions).
     * "container": heightFrame-Rect (Slide-Tops relativ zum Container).
     * "window": documentElement-Rect (.top = -window.scrollY → Slide-Tops
     * document-absolut, konsistent mit window.scrollY).
     */
    getFrameRect(): DOMRect {
        return this.scrollSource === "window"
            ? document.documentElement.getBoundingClientRect()
            : this.heightFrame.getBoundingClientRect();
    }

    getFrameDimensions() {
        // Roh-Maße je Scroll-Source (window-Branch heute inert — container überall,
        // s. ViewerChrome Iteration 3).
        let rawHeight: number;
        let width: number;
        if (this.scrollSource === "window") {
            rawHeight = grosseViewportHoehe();
            width = window.innerWidth;
        } else {
            const rect = this.heightFrame.getBoundingClientRect();
            rawHeight = rect.height;
            width = rect.width;
            // Vergroesserte Karte (28.09.2026, utils/kartenSkala): Der Viewer zieht
            // die 9:16-Karte am Desktop per transform:scale auf die Fensterhoehe.
            // rect liefert dann visuelle px — dieselbe Medizin wie im Editor:
            // Layout-px. Der visualViewport-Vorrang (else-if unten) entfaellt
            // damit ebenfalls: die Karte ist eine Box, nicht das Fenster.
            const karteSkaliert = rahmenGleichmaessigSkaliert(
                rect.width,
                rect.height,
                this.heightFrame.clientWidth,
                this.heightFrame.clientHeight,
            );
            if (this.editorMode || karteSkaliert) {
                // EDITOR (§5.2, gleiche Medizin wie in syncFrameDimensions unten):
                // Jans Interface-Zoom skaliert per transform:scale — rect liefert
                // VISUELLE px, gesetzt wird aber in LAYOUT-px. Diese Funktion gab
                // zudem ein Mischwesen zurück: Höhe aus dem (layout-korrekten)
                // Cache, Breite roh visuell → Puffer/Kamera bekamen Breite×z vs
                // Höhe×1 → Rest-Verzerrung, die mit dem Zoom skalierte.
                // clientWidth/Height = Layout-px, einheitlich mit allem anderen.
                if (this.heightFrame.clientHeight > 0) rawHeight = this.heightFrame.clientHeight;
                if (this.heightFrame.clientWidth > 0) width = this.heightFrame.clientWidth;
            }
        }
        // Per-Breite-Höhe-Cache (Plan-2 iOS-Jump-Fix, Iteration 3): Der iOS-URL-Bar-
        // Kollaps ändert die Höhe OHNE Breiten-Wechsel. Ohne Cache liefert
        // regenerateBoundsCache dann changed=true → regenerateSlidePositionsCache →
        // Slides reflowen → End-Sprung. Mit Cache bleibt die Höhe pro Breite konstant
        // → changed=false → kein Recompute → kein Sprung. NUR valide (>0) Höhen
        // cachen (sonst lockt der Cache die 3ce09c7-Null ein → Video-Timer-Crash).
        // Re-Snapshot nur bei echtem Breiten-/Orientierungswechsel. Trade-off: Slides
        // für den Initial-State dimensioniert → bei collapsed leichte Lücke unten,
        // aber KEIN Sprung (der akzeptable Failure-Mode).
        //
        // EDITOR: KEIN Hoehen-Cache (Jans Vollbild-Fund 09.09.2026). Im Pro-Editor
        // ist der Rahmen die Vorschau-Box, nicht das Fenster — eine Adressleiste
        // gibt es dort nicht. Der macOS-Vollbild-Uebergang aendert Breite UND
        // Hoehe animiert ueber viele Bilder; erreicht die Breite ihren Endwert
        // ein Bild vor der Hoehe, blieb die Hoehe des Zwischenbilds fuer immer
        // haengen (gemessen: Canvas 1147 px hoch in einer 1077-px-Box, Buehnen-
        // mitte 35 px gegen HTML-Boxen und Startbildschirm verschoben).
        if (this.rahmenIstBox) {
            if (rawHeight > 0) {
                this.cachedFrameHeight = rawHeight;
                this.cachedFrameWidth = width;
            }
            return { height: rawHeight > 0 ? rawHeight : (this.cachedFrameHeight ?? rawHeight), width };
        }
        if (
            rawHeight > 0 &&
            (this.cachedFrameHeight === null || width !== this.cachedFrameWidth)
        ) {
            this.cachedFrameHeight = rawHeight;
            this.cachedFrameWidth = width;
        }
        return { height: this.cachedFrameHeight ?? rawHeight, width };
    }

    /**
     * Synchronous remeasure after iOS bfcache restore / external-link back.
     * Prefer visualViewport over layout dvh when they diverge — otherwise the
     * sticky canvas is shorter than the shell and a dead strip appears at the bottom.
     */
    syncFrameDimensions(): boolean {
        this.cachedFrameHeight = null;
        this.cachedFrameWidth = null;

        let rawHeight: number;
        let width: number;
        const vv =
            typeof window !== "undefined" ? window.visualViewport : null;

        if (this.scrollSource === "window") {
            rawHeight = grosseViewportHoehe();
            width = vv?.width ?? window.innerWidth;
        } else {
            const rect = this.heightFrame.getBoundingClientRect();
            rawHeight = rect.height;
            width = rect.width;
            // Vergroesserte Karte (28.09.2026, utils/kartenSkala): Der Viewer zieht
            // die 9:16-Karte am Desktop per transform:scale auf die Fensterhoehe.
            // rect liefert dann visuelle px — dieselbe Medizin wie im Editor:
            // Layout-px. Der visualViewport-Vorrang (else-if unten) entfaellt
            // damit ebenfalls: die Karte ist eine Box, nicht das Fenster.
            const karteSkaliert = rahmenGleichmaessigSkaliert(
                rect.width,
                rect.height,
                this.heightFrame.clientWidth,
                this.heightFrame.clientHeight,
            );
            if (this.editorMode || karteSkaliert) {
                // EDITOR (Pro-Editor-Vorschau, Bug §5.2 Teil 2): Jans Interface-Zoom
                // skaliert den Editor per transform:scale(z). getBoundingClientRect
                // liefert dann VISUELLE px (bereits ×z) — die Höhe wird aber als
                // CSS-LAYOUT-px auf den Sticky/die Canvas gesetzt und vom transform
                // erneut ×z verkleinert. Bei 70% Zoom füllte die Canvas so nur ~70%
                // der Box, darunter schien die weiße Scroll-Seite durch.
                // clientWidth/Height sind Layout-px — die richtige Einheit hier.
                // (Der vv-Override unten entfällt im Editor ohnehin: die Box ist
                // bewusst kleiner als das Fenster, §5.2 Teil 1.)
                if (this.heightFrame.clientHeight > 0) rawHeight = this.heightFrame.clientHeight;
                if (this.heightFrame.clientWidth > 0) width = this.heightFrame.clientWidth;
            } else if (
                !this.rahmenIstBox &&
                vv?.height &&
                vv.height > 0 &&
                Math.abs(vv.height - rawHeight) > 4
            ) {
                // VERÖFFENTLICHTER Viewer: iOS-URL-Bar-Fix (Box == Fenster, der
                // visualViewport ist dort die Wahrheit). Verhalten unverändert.
                //
                // `!rahmenIstBox` ergaenzt am 21.09.2026: In einer Vorschau-BOX
                // ist der visualViewport das FENSTER und damit systematisch
                // groesser als der Rahmen — die Bedingung „weicht um mehr als
                // 4 px ab" trifft dort IMMER zu. Jeder Aufruf (pageshow,
                // Zurueck-Navigation, Viewport-Resize) setzte die Rahmenhoehe
                // dann auf die Fensterhoehe: 650 statt 442. Der Vorhang wurde
                // zu hoch und schob den START-Knopf aus dem Bild. Dass es nicht
                // immer passierte, lag daran, WANN diese Funktion laeuft —
                // Safari feuert die Ereignisse haeufiger als Chromium.
                rawHeight = vv.height;
                width = vv.width || width;
            }
        }

        if (rawHeight > 0) {
            this.cachedFrameHeight = rawHeight;
            this.cachedFrameWidth = width;
        }

        const changed = this.regenerateBoundsCache();
        if (changed) {
            this.regenerateSlidePositionsCache();
        }
        return changed;
    }

    /** @deprecated Prefer syncFrameDimensions — debounced async remeasure. */
    invalidateFrameHeightCache() {
        this.syncFrameDimensions();
    }

    regenerateBoundsCache() {
        const oldHeight = this.innerHeight;
        const oldWidth = this.innerWidth;
        const { height, width } = this.getFrameDimensions();
        this.innerHeight = height;
        this.innerWidth = width;
        this.emitHeightChanges();
        this.emitWidthChanges();
        if (!IS_HTML_MODE && oldHeight && oldWidth) {
            return this.innerHeight !== oldHeight || this.innerWidth !== oldWidth;
        }
        return true;
    }

    start = (dom: TODOAny) => {
        this.dom = dom;
        this.stopUpdate = false;
        this.regenerateBoundsCache();
        const syncTimerToScroll = () => {
            if (this.stopped) {
                return;
            }

            if (this.stopUpdate || IS_HTML_MODE || !this.dom || this.isProgrammaticScroll) {
                this.rafId = requestAnimationFrame(syncTimerToScroll);
                return;
            }

            const scrollTop = this.getScrollTop();
            const diff = scrollTop - this.scrollTop;

            if (!this.post) {
                throw new Error("Called VisualStory#start before setting post.");
            }

            const { presentationRender, slidePlayer } = this.post;

            this.scrollTop += diff;

            const { index: slideIndex, position } = this.getSlidePositionAtScroll(this.scrollTop);
            const timeDiff = +new Date() - this.lastSlideTimestamp;

            if (this.lastSlideIndex !== slideIndex) {
                this.lastSlideIndex = slideIndex;
                this.lastSlideTimestamp = +new Date();
                this.onEvent?.("storyNavigation", {
                    slide: slideIndex,
                    totalSlides: presentationRender.slides.length,
                    type: "scroll",
                });
            }

            const scrollingFast = Math.abs(slidePlayer.currentSlide - slideIndex) > FAST_SCROLL_SLIDES_AHEAD;
            const scrollingSlow = timeDiff > SLOW_SCROLL_TIMEOUT;

            if (scrollingFast) {
                this.fastScroll = true;
            }

            if (scrollingSlow) {
                this.fastScroll = false;
            }

            const animationSlide = presentationRender.slides[slideIndex];
            const previousSlide =
                slideIndex > 0 ? presentationRender.slides[slideIndex - 1] : presentationRender.slides[slideIndex];
            // using textcenter works much better than position.animationArea.from
            // with this, the prev/next buttons work as expected when a slide is scrollable
            const timer = linearScale(
                [position.animateAreaFrom, position.textCenter],
                [previousSlide.from, animationSlide.from],
                { clamp: true }
            )(this.scrollTop);

            // Zweiter, PLATEAU-FREIER Zeitwert — nur fuer Scroll-Videos
            // (WebGLAssets.scrollSmoothTimer, Begruendung dort).
            //
            // Der echte Zeitgeber oben endet bei `textCenter` und klemmt dann
            // fest, solange die Slide zum Lesen stehenbleibt. Genau das ist
            // Jans "Einrasten": das Video steht mitten auf dem Slide still.
            // Hier laeuft dieselbe Zeitspanne stattdessen bis zum Beginn des
            // NAECHSTEN Animationsbereichs, also ueber die gesamte
            // Scrollstrecke der Slide. An den Slide-Grenzen liefern beide
            // denselben Wert; dazwischen laeuft dieser gleichmaessig weiter.
            WebGLAssets.scrollSmoothTimer = computeScrollSmoothTimer({
                scrollTop: this.scrollTop,
                animateAreaFrom: position.animateAreaFrom,
                top: position.top,
                height: position.height,
                nextAnimateAreaFrom: this.slidePositions[slideIndex + 1]?.animateAreaFrom,
                prevFrom: previousSlide.from,
                slideFrom: animationSlide.from,
                fallback: timer,
            });
            // always use scrolling
            if (this.editorMode && WebGLAssets.onEditorScrollTimer) {
                // EDITOR: zwei Timer-Quellen müssen konsistent bleiben — der scroll-
                // abgeleitete `timer` und der MASTER (slidePlayer.timer, vom Adapter aus
                // Jans Timeline/Play gespiegelt). Wir unterscheiden über WER sich zuerst
                // bewegt hat, um einen Feedback-Fight (Play advance ↔ Scroll-Sync) zu
                // vermeiden:
                //   • Master bewegt (Timeline/Play/Scrub) → Viewer nachscrollen, damit die
                //     scroll-positionierten Text-Overlays (.slide-content) dem aktuellen
                //     Slide folgen. NICHT zurückrouten (sonst würde die Play-Animation
                //     jeden Frame auf die Scroll-Position zurückgesetzt → Stillstand).
                //   • Master stand, aber der User hat gescrollt (diff !== 0) → Master aus
                //     dem Scroll treiben (bisheriges Verhalten, Preview-Scrub).
                const master = slidePlayer.timer;
                // Frame-Groesse geaendert (Formatwechsel, Zoom)? Dann ist die
                // Scroll-Position dieses Frames nicht mehr in derselben Einheit
                // wie die (noch alten) Slide-Positionen — nicht als User-Scroll
                // deuten, sondern nach der Neuvermessung zum Playhead scrollen.
                const frameW = this.heightFrame.clientWidth;
                const frameH = this.heightFrame.clientHeight;
                if (frameW !== this.lastEditorFrameW || frameH !== this.lastEditorFrameH) {
                    if (this.lastEditorFrameW !== 0 || this.lastEditorFrameH !== 0) {
                        this.editorRealignFrames = EDITOR_REALIGN_FRAMES;
                    }
                    this.lastEditorFrameW = frameW;
                    this.lastEditorFrameH = frameH;
                }
                const realign = this.editorRealignFrames > 0;
                if (realign) {
                    this.editorRealignFrames -= 1;
                }
                const masterMoved =
                    realign ||
                    (!Number.isNaN(this.lastEditorMasterTimer) &&
                        Math.abs(master - this.lastEditorMasterTimer) > 0.5);
                this.lastEditorMasterTimer = master;
                if (masterMoved) {
                    const target = this.getScrollForTimer(master);
                    if (target != null && Math.abs(target - this.scrollTop) > 1) {
                        this.setScrollTopValue(target);
                        // WICHTIG: den vom Browser TATSÄCHLICH angewandten (gerundeten)
                        // scrollTop zurücklesen und speichern. Sonst ergibt der Float-
                        // Target vs. der gerundete DOM-Wert nächsten Frame ein diff ≠ 0,
                        // das (wenn der Master-Mirror einen Frame nachhinkt) als User-
                        // Scroll fehlinterpretiert würde → onEditorScrollTimer → Jans
                        // setTimer setzt running=false → Play würde sofort stoppen.
                        this.scrollTop = this.getScrollTop();
                    }
                } else if (diff !== 0) {
                    WebGLAssets.onEditorScrollTimer(timer);
                }
            } else {
                slidePlayer.setTimer(timer);
            }
            this.emitBeforeStoryChanges();
            this.emitAfterStoryChanges();
            this.rafId = requestAnimationFrame(syncTimerToScroll);
        };

        if (!IS_HTML_MODE) {
            window.addEventListener("resize", this.debouncedUpdate);
        }

        // This is an ugly hack which should be resolved later.
        // The problem is due to the innerHeight usage instead of 100vh we
        // make sure to initalise the positions AFTER regenerateBoundsCache
        // is called which means the component rerenders.
        // TODO Investigage whether this hack is still needed
        setTimeout(() => {
            this.regenerateSlidePositionsCache();
            setTimeout(() => {
                syncTimerToScroll();
                // registering the styles will change the positions, so we need to reapply this
                setTimeout(() => {
                    this.regenerateSlidePositionsCache();
                }, 10);
            });
        });
        this.stopped = false;
    };

    regenerateSlidePositionsCache() {
        if (!this.dom || this.isProgrammaticScroll) {
            return;
        }
        // Editor: Positionen aendern sich → beim naechsten Sync-Frame die
        // Vorschau zum Playhead scrollen statt den Playhead zu verschieben.
        if (this.editorMode) {
            this.editorRealignFrames = EDITOR_REALIGN_FRAMES;
        }
        const scrollTop = this.getScrollTop(); // window.pageYOffset || document.documentElement.scrollTop;

        const articleRect = this.dom.getElementsByClassName("article-container")[0].getBoundingClientRect();
        // Container mode: (bcr.top − rectFrame.top) gives position relative to
        // the container's visible edge → add scrollTop to get scroll-space.
        // Window mode: rectFrame.top = −scrollY, so the subtraction already
        // yields the page-absolute position → adding scrollTop double-counts.
        // MASSSTAB der Buehne (s. utils/positionsSkala): in der Galerie-
        // Hover-Vorschau steckt der Viewer in einem `transform: scale(...)`.
        // Ohne Umrechnung wandern die Positionen bei jeder Neuvermessung
        // waehrend der Fahrt (Jans „zuckt zwei Mal", 23.09.2026).
        const skala =
            this.scrollSource === "window"
                ? 1
                : positionsSkala(
                      this.viewerFrame.getBoundingClientRect().height,
                      this.viewerFrame.clientHeight,
                  );
        const scrollTopForPosition = this.scrollSource === "window" ? 0 : scrollTop;
        // Gemessene Bildschirm-Abstaende in LAYOUT-Pixel umrechnen — dieselbe
        // Einheit wie `scrollTop` und wie die HTML-Textboxen, die im
        // gescrollten DOM liegen. (23.09.2026, Nachbesserung zu #1007: dort
        // wurde umgekehrt der Scroll-Weg in Bildschirm-Pixel gerechnet. Die
        // Fahrt lief damit ruhig, aber der Zeitgeber lief gegenueber den
        // mitscrollenden Textboxen zu schnell — Jans „nicht synchron mit den
        // HTML-Boxen".)
        const inLayout = (px: number) => (skala === 1 ? px : px / skala);
        const slides = this.dom.getElementsByClassName("slide-container");

        if (!this.post) {
            throw new Error("Called VisualStory#initialisePositions before setting post.");
        }

        const { renderer, presentationRender } = this.post;

        const slidePositions: Array<SlidePosition> = [];
        const rectFrame = this.getFrameRect();
        for (let i = 0; i < slides.length; i += 1) {
            const { safeAreaSizeXRelative, bounds } = renderer.calculateBounds();
            const presentationSlide = presentationRender.slides[i];
            // Editor: beim Slide-Einfuegen rendert React sofort einen weiteren
            // .slide-container, waehrend presentationRender noch den alten
            // Stand traegt — slides[i] ist dann undefined und `.options` warf
            // (Jans Fund 25.08.2026: Editor-Absturz bei "Insert slide here"
            // und im SLIDE-EDIT-Modus). Container ohne Store-Slide hier
            // ueberspringen; der 10-ms-Doppellauf vermisst gleich vollstaendig.
            if (!presentationSlide) {
                continue;
            }
            const dom = slides[i];
            const animateArea = dom.querySelector(".slide-animate");
            const contentArea: HTMLDivElement = dom.querySelector(".slide-content")!;
            const boundingClientRect = dom.getBoundingClientRect();
            const rect: { top: number; height: number } = {
                // top relative to the viewer frame (not the viewport), in Layout-Pixeln
                top: inLayout(boundingClientRect.top - rectFrame.top),
                height: inLayout(boundingClientRect.height),
            };
            const boundingClientRectAnimate = animateArea!.getBoundingClientRect();
            const rectAnimate: { top: number; height: number } = {
                top: inLayout(boundingClientRectAnimate.top - rectFrame.top),
                height: inLayout(boundingClientRectAnimate.height),
            };
            const rohContent = contentArea
                ? contentArea.getBoundingClientRect()
                : {
                      height: 0,
                      width: 0,
                  };
            const rectContent = { height: inLayout(rohContent.height), width: rohContent.width };
            const { height } = rect;
            const top = rect.top + scrollTopForPosition;

            const useMobile = bounds.newWidth < slidePositionBreakpoints.ipad;

            const position = calculateSlidePosition(
                presentationSlide.options || presentationRender.presentationOptions.slideOptions,
                safeAreaSizeXRelative,
                bounds.newWidth,
                350,
                300,
                useMobile
            );
            const animateAreaFrom = rectAnimate.top + scrollTopForPosition;

            slidePositions.push({
                // Lese-Rast: Slides MIT Box klemmen den Zeitgeber ab textCenter,
                // bis die Box durchgescrollt ist (Viewport/2 − Boxhöhe/2 vor dem
                // Sektionsende). Slides OHNE Box bekamen 0 — die Animation lief
                // ohne Halt in den nächsten Slide. Jetzt eine kurze feste Rast
                // (Jans Wunsch 13.10.2026: „auch ohne HTML-Box kurz stehenbleiben").
                // Checkbox „Show HTML box" AUS = bewusst durchlaufen lassen
                // (Jans Regel 13.10.2026): nur ABGEWAEHLTE Boxen laufen ohne
                // Rast; eine angehakte, aber leere Box (der Normalfall ohne
                // Boxtext) bekommt die kurze Rast.
                // KEINE Rast auf dem TITEL-Slide (Jans Nav-Fund 13.10.2026):
                // dessen textCenter ist das Ziel von "zurueck zu START" und
                // liegt direkt hinter dem Cover — eine Rast davor liesse das
                // dunkle Cover-Ende als Balken oben im Bild stehen.
                textCenter:
                    top +
                    rectAnimate.height -
                    (contentArea
                        ? this.innerHeight / 2 - rectContent.height / 2
                        : i > 0 && (presentationSlide.text?.show ?? true)
                          ? RAST_OHNE_BOX * this.innerHeight
                          : 0),
                animateAreaFrom,
                top,
                height,
            });
            if (i === slides.length - 1) {
                const maxScroll = this.getMaxScroll();

                this.creditsScrollOffset = Math.min(top + height + this.innerHeight, maxScroll);
            }

            if (contentArea) {
                if (useMobile) {
                    contentArea.style.transform = "";
                    contentArea.style.width = "100%";
                    // Inline-Schriftgroesse der BREITEN Buehne verwerfen (Jans
                    // Fund 11.09.2026): Vollbild-Umschalter ab 1600 px setzt
                    // unten `fontSize = grund × 2,11` inline; zurueck im
                    // Handyrahmen wurde nur Breite/Transform zurueckgesetzt —
                    // die Box blieb mit 38 px Schrift stehen (gemessen).
                    contentArea.style.fontSize = "";
                } else {
                    const translateX =
                        position.left !== undefined
                            ? position.left + position.margin
                            : articleRect.width - position.right! - rectContent.width - position.margin;
                    contentArea.style.transform = `translateX(${translateX}px)`;
                    contentArea.style.width = `${position.width}px`;
                    // GEBRANDETE GRUNDGROESSE NICHT VERWERFEN (Laura/WESER-KURIER
                    // 2026-08-18: "wird auch in der Vorschau im Editor nicht
                    // richtig angezeigt").
                    //
                    // Vorher stand hier `getSlideFontSizeByScreenWidth(...)`, das
                    // IMMER eine Inline-Groesse in `em` setzte — unterhalb von
                    // 1600 px schlicht `1em`. Inline schlaegt Stylesheet, damit war
                    // die Regel `.slide-content { font-size: <fontBaseSize>px }`
                    // (VisualStoryPlayer, aus dem Branding) oberhalb von 620 px
                    // Containerbreite ausgehebelt: `1em` loest gegen die Groesse
                    // des UMGEBENDEN Elements auf. Im freistehenden Viewer sind das
                    // zufaellig ebenfalls 16 px, im Editor-Geruest kann es etwas
                    // anderes sein — dann laufen Vorschau und geteilter Scrolly
                    // auseinander. Am Live-Dokument gemessen: inline "1em",
                    // berechnet 16px, Elterngroesse 16px.
                    //
                    // Jetzt: bei Multiplikator 1 gar nichts setzen (die
                    // Stylesheet-Regel gilt, also die gebrandete Groesse), sonst
                    // die Vergroesserung fuer sehr breite Buehnen in PIXELN aus
                    // derselben Grundgroesse rechnen statt vom Host zu erben.
                    const multiplikator = responsiveMultiplier(bounds.newWidth);
                    if (multiplikator === 1) {
                        contentArea.style.fontSize = "";
                    } else {
                        const rohGrund = (
                            presentationRender.presentationOptions.slideOptions as
                                | { fontBaseSize?: unknown }
                                | undefined
                        )?.fontBaseSize;
                        const grundGroesse =
                            typeof rohGrund === "number" && Number.isFinite(rohGrund) && rohGrund > 0
                                ? rohGrund
                                : 18;
                        contentArea.style.fontSize = `${grundGroesse * multiplikator}px`;
                    }
                }
            }
        }

        this.slidePositions = slidePositions;
    }

    getSlidePositionAtScroll(scrollTop: number): {
        index: number;
        position: SlidePosition;
    } {
        if (this.slideCount === 0) {
            return {
                index: 0,
                position: {
                    top: 0,
                    animateAreaFrom: 0,
                    textCenter: 0,
                    height: 0,
                },
            };
        }
        let slidePositionIndex = Math.max(
            0,
            this.slidePositions.findIndex(({ top, height }) => top >= scrollTop - height)
        );

        if (scrollTop > this.slidePositions.at(-1)!.top) {
            slidePositionIndex = this.slideCount - 1;
        }

        return {
            index: slidePositionIndex,
            position: this.slidePositions[slidePositionIndex],
        };
    }

    /**
     * Editor-Text-Sync: der Timer, den eine gegebene Scroll-Position ergäbe —
     * reine (seiteneffektfreie) Kopie des Vorwärts-Mappings aus syncTimerToScroll.
     */
    private timerForScrollTop(scrollTop: number): number {
        const { index, position } = this.getSlidePositionAtScroll(scrollTop);
        const slides = this.post!.presentationRender.slides;
        const slide = slides[index];
        const prev = index > 0 ? slides[index - 1] : slides[index];
        return linearScale([position.animateAreaFrom, position.textCenter], [prev.from, slide.from], {
            clamp: true,
        })(scrollTop);
    }

    /**
     * Editor-Text-Sync: inverse Abbildung timer→scrollTop per Binärsuche über die
     * (monotone) Vorwärtsfunktion timerForScrollTop. Reuse der Vorwärtsfunktion
     * garantiert Konsistenz mit dem Reader-Scroll-Verhalten. Nur im Editor genutzt.
     */
    getScrollForTimer(targetTimer: number): number | null {
        if (!this.post || this.slidePositions.length === 0) {
            return null;
        }
        const max = this.getMaxScroll();
        if (!(max > 0)) {
            return null;
        }
        let lo = 0;
        let hi = max;
        for (let k = 0; k < 26; k += 1) {
            const mid = (lo + hi) / 2;
            if (this.timerForScrollTop(mid) < targetTimer) {
                lo = mid;
            } else {
                hi = mid;
            }
        }
        return (lo + hi) / 2;
    }

    stop() {
        window.removeEventListener("resize", this.debouncedUpdate);
        if (this.cancelScroll) {
            this.cancelScroll();
        }
        this.stopUpdate = true;
        this.stopped = true;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    setPost(post: PlayablePresentation | undefined) {
        this.post = post;
    }

    handleSlideSpeed() {
        clearTimeout(this.makeSlideSlowAgainTimeout);
        const lastClick = Number(new Date()) - Number(this.lastButtonClick);
        // to consecutive click in less than 500ms
        const fast = lastClick < FAST_CLICK;
        this.fastClick = this.fastClick || fast;
        // if (this.fastClick) {
        //     this.makeSlideSlowAgainTimeout = setTimeout(() => {
        //         runInAction(() => {
        //             this.fastClick = false;
        //         });
        //     }, SLOW_CLICK_TIMEOUT);
        // }
        this.lastButtonClick = new Date();
    }

    /**
     * Basis-Slide fuer die Nav-Knoepfe. NICHT slidePlayer.currentGoalSlide:
     * der haengt an goalTime, und die friert ein, sobald der Scroll->Timer-
     * Sync aus irgendeinem Grund nicht laeuft (Jans Fund 25.08.2026 in der
     * Pro-Editor-Vorschau: "runter" rechnete ewig 0->1 und tat sichtbar
     * nichts). Die tatsaechliche Scroll-Position ist im scrollgetriebenen
     * Leser die Wahrheit und wird hier synchron aus dem DOM gelesen.
     * Waehrend ein Knopf-Tween laeuft, zaehlt stattdessen dessen Ziel,
     * damit schnelles Mehrfachklicken weiterschaltet statt neu zu messen.
     */
    private knopfBasisIndex(): number {
        if (this.knopfZielIndex !== null) {
            return this.knopfZielIndex;
        }
        return this.getSlidePositionAtScroll(this.getScrollTop()).index;
    }

    paginateSlide(oldSlideIndex: number, newSlideIndex: number) {
        const newSlidePosition = this.slidePositions[newSlideIndex];

        if (!newSlidePosition || !this.post) {
            return;
        }

        const { slides } = this.post.presentationRender;
        const oldSlide = slides[oldSlideIndex];
        const newSlide = slides[newSlideIndex];

        const animationDuration = Math.max(Math.abs(oldSlide.from - newSlide.from) + SCROLL_TIME_OFFSET, SCROLL_SPEED);

        const newTop = newSlidePosition.textCenter;

        // Laufenden Tween IMMER erst beenden — zwei parallele rAF-Schleifen
        // schreiben sonst abwechselnd alte und neue Zielkurve in scrollTop
        // (sichtbares Vor-und-Zurück-Zucken beim Weiterschalten).
        this.cancelScroll?.();
        this.knopfZielIndex = newSlideIndex;
        const tween = scrollTo(newTop, {
            context: this.getScrollContext(),
            duration: animationDuration,
            easing: Easings.quadInOut,
        });
        this.cancelScroll = tween?.cancel;
        tween?.promise.finally(() => {
            if (this.knopfZielIndex === newSlideIndex) {
                this.knopfZielIndex = null;
            }
        });
    }

    /**
     * Zielpositionen der Slides (textCenter, auf das Scroll-Ende begrenzt) —
     * Grundlage der Nav-Knoepfe, s. utils/navZiel.
     */
    private navZiele(): number[] {
        const max = this.getMaxScroll();
        return this.slidePositions.map((p) => Math.min(p.textCenter, max));
    }

    /** Scroll-Position, von der ein Knopfdruck ausgeht (laufender Tween: sein Ziel). */
    private knopfBasisScroll(): number {
        if (this.knopfZielIndex !== null) {
            const p = this.slidePositions[this.knopfZielIndex];
            if (p) return Math.min(p.textCenter, this.getMaxScroll());
        }
        return this.getScrollTop();
    }

    private navigiere(richtung: "hoch" | "runter") {
        this.handleSlideSpeed();
        if (!this.post) {
            throw new Error(`Called VisualStory#${richtung === "hoch" ? "previousSlide" : "nextSlide"} before setting post.`);
        }
        const basis = this.knopfBasisScroll();
        // Titel-Slide-Ziel direkt hinterm Cover (Höhe innerHeight) = START
        const ziel = navZiel(this.navZiele(), basis, richtung, undefined, this.innerHeight);
        if (ziel === "credits") {
            this.scrollToCredits();
            return;
        }
        if (ziel === "start") {
            this.scrollZumStart();
            return;
        }
        const alt = this.knopfBasisIndex();
        this.paginateSlide(Math.min(alt, this.slideCount - 1), ziel);
    }

    /** Zurueck auf den Startbildschirm (Cover) — der Hoch-Pfeil am ersten Slide. */
    scrollZumStart() {
        this.cancelScroll?.();
        this.knopfZielIndex = null;
        this.cancelScroll = scrollTo(0, {
            context: this.getScrollContext(),
            duration: SCROLL_SPEED,
            easing: Easings.quadInOut,
        })?.cancel;
    }

    previousSlide() {
        this.navigiere("hoch");
    }

    nextSlide() {
        this.navigiere("runter");
    }

    scrollToStartSlide(opts?: { intro?: boolean }) {
        this.handleSlideSpeed();
        // Der Intro-Tween (2,2 s programmatischer Scroll zur ersten Text-
        // Position) ist ein LESER-Feature des veröffentlichten Scrollys. Im
        // Editor lief er beim Öffnen mit (autoStart des geteilten Viewers):
        // der Playhead wanderte sichtbar bis ~0,7 s und blieb dort stehen
        // (Jans Fund #1) — der Editor muss aber ruhig bei 0 öffnen wie Jans
        // Original. Viewer-Verhalten unverändert.
        if (opts?.intro && !this.editorMode) {
            this.playIntroSequence();
            return;
        }
        // EDITOR: gar nicht scrollen. goToSlide(0) faehrt animiert zu
        // slide[0].textCenter (> 0) — im Editor war das beim Oeffnen als
        // kleiner Ruck nach unten sichtbar (Jans Fund #5), obwohl dort die
        // TIMELINE den Timer treibt und der Scroll-Container ruhig bei 0
        // stehen soll. Reader-Verhalten unveraendert.
        if (this.editorMode) {
            return;
        }
        this.goToSlide(0);
    }

    /**
     * Scroll target for the post-cover intro tween.
     * Generic scrollys: slide[1].textCenter (first content animation).
     * Sentence-Bloom: ~3 words into the slide-bloom runway, not its end.
     */
    resolveIntroScrollTarget(): number | undefined {
        // post is PlayablePresentation — slides live on presentationRender, not post.data.data.
        const slides = this.post?.presentationRender?.slides ?? [];
        const bloomIdx = slides.findIndex((s) => s?.id === 'slide-bloom');
        if (bloomIdx >= 0) {
            const pos = this.slidePositions[bloomIdx];
            if (!pos) return undefined;

            const quoteText = slides.find((s) => s?.id === 'slide-2')?.text?.text ?? '';
            const wordCount = quoteText
                .replace(/<[^>]+>/g, ' ')
                .trim()
                .split(/\s+/)
                .filter(Boolean).length;
            const totalWords = Math.max(wordCount, 1);
            // Match RendererWebGL typewriter: floor(progress * (totalWords + 0.5))
            const fraction = Math.min(
                0.2,
                (SENTENCE_BLOOM_INTRO_VISIBLE_WORDS + 0.5) / (totalWords + 0.5),
            );
            const runway = pos.textCenter - pos.animateAreaFrom;
            return pos.animateAreaFrom + runway * fraction;
        }

        return (this.slidePositions[1] ?? this.slidePositions[0])?.textCenter;
    }

    /**
     * Intro-Tween nach Cover-Start: scrollt programmatisch zu
     * Slide-1.textCenter (Fallback Slide-0 wenn Single-Slide). Dauert
     * INTRO_DURATION ms, easing quadInOut. User-Input (touchstart, wheel,
     * mousedown, keydown) cancelt den Tween sofort und detacht die
     * Listener — verhindert das „User vs. Tween"-Race, das Plan-N-Resume
     * auf der UX-Ebene gekillt hatte.
     *
     * Sentence-Bloom exception: presentation slide index 1 is `slide-bloom`
     * (the typewriter runway ending at bloomEnd). Its textCenter is the END of
     * the quote — scrolling there shows almost the full text. Bloom intro
     * stops ~3 words into the runway instead.
     *
     * Listener cleanup auch bei natürlichem Tween-Ende (scroll.promise.finally).
     */
    playIntroSequence() {
        const target = this.resolveIntroScrollTarget();
        if (target == null) return;

        const maxScroll = this.getMaxScroll();
        const safeTop = Math.min(target, maxScroll);

        const cancel = () => {
            this.cancelScroll?.();
            detach();
        };
        const detach = () => {
            this.viewerFrame.removeEventListener("wheel", cancel);
            this.viewerFrame.removeEventListener("touchstart", cancel);
            this.viewerFrame.removeEventListener("mousedown", cancel);
            window.removeEventListener("keydown", cancel);
        };

        this.viewerFrame.addEventListener("wheel", cancel, { passive: true });
        this.viewerFrame.addEventListener("touchstart", cancel, { passive: true });
        this.viewerFrame.addEventListener("mousedown", cancel);
        window.addEventListener("keydown", cancel);

        // INTRO IM SLIDE-TEMPO (Jans Tempo-Regler 13.10.2026): der Tween
        // faehrt bis Slide-2-textCenter — also genau ueber die Dauer des
        // Titel-Slides. Vorher fix 2000 ms (kuratiert im 1000er-Raster);
        // jetzt die ECHTEN ms der Timer-Spanne, damit der Leser dasselbe
        // Tempo sieht wie das Pro-Editor-Play (4-s-Slide ⇒ 4-s-Intro).
        // Sentence-Bloom behaelt die kuratierte Konstante (eigenes Ziel
        // mitten in der Schreibmaschinen-Strecke).
        const introSlides = this.post?.presentationRender?.slides ?? [];
        const istBloom = introSlides.some((sl) => sl?.id === "slide-bloom");
        const introSpanne =
            ((introSlides[1]?.from ?? 0) as number) - ((introSlides[0]?.from ?? 0) as number);
        // COVER-ANTEIL DAZURECHNEN (Jans Fund 15.10.2026, „startet viel zu
        // schnell, obwohl 1,6 s eingestellt"): Der Tween muss in derselben Zeit
        // auch das Cover wegscrollen (Zeitgeber steht dabei still) — die
        // Animation selbst lief so in ~0,9 statt 1,6 s. Jetzt wird die Dauer
        // um den Anteil der Gesamtstrecke verlängert, auf dem die Zeitleiste
        // läuft: dort entspricht das Tempo wieder der Einstellung.
        // Exakt über die quadInOut-Kurve: Anteil der Strecke ohne Zeitleiste
        // (Cover) → Zeitpunkt t0, an dem der Tween ihn hinter sich hat; die
        // Zeitleiste läuft dann (1 − t0) der Dauer.
        const zielPos = this.slidePositions[1];
        const startScroll = this.getScrollTop();
        const gesamt = safeTop - startScroll;
        const tot = zielPos ? Math.max(0, zielPos.animateAreaFrom - startScroll) : 0;
        const f0 = gesamt > 0 ? Math.min(Math.max(tot / gesamt, 0), 0.9) : 0;
        const t0 = f0 < 0.5 ? Math.sqrt(f0 / 2) : 1 - Math.sqrt((1 - f0) / 2);
        const streckung = Math.min(1 / (1 - t0), 3);
        const scroll = scrollTo(safeTop, {
            context: this.getScrollContext(),
            duration: !istBloom && introSpanne > 0 ? Math.round(introSpanne * streckung) : INTRO_DURATION,
            easing: Easings.quadInOut,
        });
        this.cancelScroll = scroll?.cancel;
        void scroll?.promise.finally(detach);
    }

    /**
     * ECHTZEIT-WIEDERGABE (Jans „Sonderfall" 09.08.2026): Scrollys mit
     * WIEDERGABE-Modus (Pro-Editor, genau Start- + End-Slide) sind keine
     * Lese-Strecken, sondern Animationen mit fester Dauer. Der normale
     * Intro-Tween spielte sie in INTRO_DURATION (2 s) komplett ab — eine
     * 8,7-Sekunden-Timeline samt Ton rauschte durch. Hier läuft stattdessen
     * eine Uhr in echten Millisekunden über die Slide-Timings, und der
     * Scroll wird pro Frame über die exakte Umkehrfunktion nachgezogen
     * (getScrollForTimer) — Keyframes und Audio laufen dadurch in Echtzeit,
     * über denselben Scroll→Timer-Pfad wie beim lesenden Nutzer.
     *
     * once     → einmal bis zum Ende, dann stehen lassen (frei scrollbar).
     * loop     → am Ende zurück an den Anfang, endlos.
     * pingpong → am Ende rückwärts, endlos hin und her.
     *
     * Nutzereingabe (Rad, Touch, Maus, Taste) beendet die Wiedergabe —
     * dieselbe Höflichkeitsregel wie beim Intro-Tween. Registriert sich als
     * cancelScroll, damit auch Slide-Knöpfe/Credits-Sprung sie beenden.
     */
    startRealtimePlayback(
        mode: "once" | "loop" | "pingpong",
        /** Schleife: unmittelbar VOR dem Rücksprung an den Anfang gerufen,
         *  solange noch das letzte Bild steht — der Viewer legt dort eine
         *  weiche Überblendung an (Jan 17.10.2026, s. viewer/schleifenUeberblendung). */
        beimSchleifenNeustart?: () => void,
    ) {
        // Im Editor treibt die Timeline; dort darf kein zweiter Fahrer ran
        // (gleiche Regel wie scrollToStartSlide).
        if (this.editorMode) return;
        const slides = this.post?.presentationRender?.slides ?? [];
        if (slides.length < 2 || this.slidePositions.length < 2) return;
        // slide.from = END-Zeitpunkt der Slide (ms). Der Reader spielt beim
        // Scrollen genau das Segment [slides[0].from … slides[-1].from] ab
        // (s. Timer-Mapping in syncTimerToScroll) — dieselbe Spanne fährt
        // hier die Uhr ab.
        const t0 = slides[0].from;
        const t1 = slides[slides.length - 1].from;
        if (!(t1 - t0 > 0)) return;

        // Nie vor die Story springen: unterhalb von slide[0].textCenter
        // gehört der Scroll dem Cover (beforeStory) — der Loop-Rücksprung
        // landet deshalb dort, wo auch scrollToStartSlide nach dem Start
        // steht.
        const minScroll = this.slidePositions[0].textCenter;

        let raf: number | null = null;
        const cancel = () => {
            if (raf != null) cancelAnimationFrame(raf);
            raf = null;
            detach();
        };
        const detach = () => {
            this.viewerFrame.removeEventListener("wheel", cancel);
            this.viewerFrame.removeEventListener("touchstart", cancel);
            this.viewerFrame.removeEventListener("mousedown", cancel);
            window.removeEventListener("keydown", cancel);
        };
        this.viewerFrame.addEventListener("wheel", cancel, { passive: true });
        this.viewerFrame.addEventListener("touchstart", cancel, { passive: true });
        this.viewerFrame.addEventListener("mousedown", cancel);
        window.addEventListener("keydown", cancel);

        // Laufenden Tween erst beenden — Begründung in paginateSlide.
        this.cancelScroll?.();
        this.cancelScroll = cancel;

        let t = t0;
        let dir: 1 | -1 = 1;
        let last = performance.now();
        const apply = () => {
            const target = this.getScrollForTimer(t);
            if (target == null) return;
            this.setScrollTopValue(Math.max(target, minScroll));
        };
        const step = (now: number) => {
            // Tab-Rückkehr: rAF stand still, `now - last` wäre riesig — ein
            // Deckel hält den Wiedereinstieg sanft statt Sprung ans Ende.
            const dt = Math.min(now - last, 100);
            last = now;
            t += dir * dt;
            if (t >= t1) {
                if (mode === "once") {
                    t = t1;
                    apply();
                    cancel();
                    return;
                }
                if (mode === "loop") {
                    try {
                        beimSchleifenNeustart?.();
                    } catch {
                        // Überblendung ist Kür — die Schleife läuft auf jeden Fall weiter
                    }
                    t = t0;
                } else {
                    t = t1;
                    dir = -1;
                }
            } else if (t <= t0 && dir < 0) {
                t = t0;
                dir = 1;
            }
            apply();
            raf = requestAnimationFrame(step);
        };
        apply();
        raf = requestAnimationFrame(step);
    }

    scrollToCredits() {
        this.handleSlideSpeed();

        const target = this.creditsScrollOffset;
        this.isProgrammaticScroll = true;

        // Laufenden Tween erst beenden — Begründung in paginateSlide.
        this.cancelScroll?.();
        this.cancelScroll = scrollTo(target, {
            context: this.getScrollContext(),
            duration: SCROLL_SPEED,
            easing: Easings.quadOut,
        })?.cancel;

        // Safari-Fix
        setTimeout(() => {
            requestAnimationFrame(() => {
                const maxScroll = this.getMaxScroll();

                const safeTarget = Math.min(target, maxScroll);

                this.setScrollTopValue(safeTarget);
                this.scrollTop = safeTarget;
                this.isProgrammaticScroll = false;
            });
        }, SCROLL_SPEED + 50);
    }

    /**
     * @param glide Auto-Play-Gleitfahrt (Galerie-Hover-Vorschau): Dauer in ms,
     * LINEAR statt quadOut. Standard (undefined) bleibt der Knopf-Sprung des
     * Readers. Warum: Der Stufen-Takt „1,4s-Tween mit Abbremsen + 0,4s
     * Stillstand" las sich bei Zoom-/Masken-Inhalten als Zucken („kurz
     * doppelt gezoomt", „Maske läuft nicht durch" — Jan 08.08.2026); per
     * Frame-Messung war die Wiedergabe dabei technisch flüssig (p99 9,3 ms).
     * Mit glide = Schrittintervall endet jeder Tween exakt beim Start des
     * nächsten → durchgehend konstante Geschwindigkeit wie ein gleichmäßig
     * scrollender Leser.
     */
    goToSlide(currentSlideIndex: TODOAny, glide?: number) {
        this.handleSlideSpeed();
        const slide = this.slidePositions[currentSlideIndex];
        if (!slide) return;

        const maxScroll = this.getMaxScroll();
        const safeTop = Math.min(slide.textCenter, maxScroll);

        // Laufenden Tween erst beenden — Begründung in paginateSlide.
        this.cancelScroll?.();
        this.cancelScroll = scrollTo(safeTop, {
            context: this.getScrollContext(),
            duration: glide ?? SCROLL_SPEED,
            easing: glide ? Easings.linear : Easings.quadOut,
        })?.cancel;
    }

    /**
     * Sprung über die Kapitel-Punkte — im Tempo der Timeline wie die
     * Nav-Knöpfe, statt in 1,4 s durch den ganzen Scrolly zu rasen (Jans
     * Wahl 06.10.2026 „Schnitt, dann Timeline“):
     * - Ziel höchstens SPRUNG_NAH Slides entfernt: hinlaufen, jede Slide in
     *   ihrer Timeline-Länge.
     * - Weiter weg: Schnitt auf die Slide vor dem Ziel, dann nur noch deren
     *   Übergang ins Ziel in echter Länge — auch rückwärts, damit man das
     *   Ziel ankommen sieht statt es rückwärts abzuspielen.
     * - `durchlaufen`: nie schneiden — die Kapitel-Punkte setzen es für Ziele
     *   im selben Kapitel (Jan: „innerhalb eines Kapitels durchlaufen“).
     * Läufe können so 20 s dauern: Scrollen, Tippen, Klicken oder eine Taste
     * beendet sie (dieselbe Höflichkeitsregel wie beim Intro-Tween) —
     * Trackpad-Nachschwung vom Scrollen vor dem Klick zählt nicht (radAbsicht).
     */
    springeZuSlide(index: number, durchlaufen = false) {
        const ziel = this.slidePositions[index];
        if (!ziel || !this.post) return;
        const { slides } = this.post.presentationRender;
        const von = this.getSlidePositionAtScroll(this.getScrollTop()).index;
        this.handleSlideSpeed();
        // Laufenden Tween erst beenden — Begründung in paginateSlide
        this.cancelScroll?.();
        let ab = von;
        if (!durchlaufen && Math.abs(index - von) > SPRUNG_NAH && index > 0) {
            ab = index - 1;
            // Nie vor den Story-Anfang schneiden (Startbildschirm: Nav und
            // Punkte blendeten aus, s. beforeStory) — für die erste Slide
            // beginnt der Übergang dann eben am Story-Anfang
            const schnitt = Math.max(this.slidePositions[ab].textCenter, this.innerHeight);
            this.setScrollTopValue(Math.min(schnitt, this.getMaxScroll()));
        }
        // Jede Slide so lang wie ein Nav-Knopf-Schritt (Timeline + Totzone);
        // über mehrere Slides gleichmäßig statt quadInOut (s. gleichmaessig)
        const schritte = Math.abs(index - ab);
        const dauer = Math.max(
            Math.abs(slides[index].from - slides[ab].from) + SCROLL_TIME_OFFSET * Math.max(1, schritte),
            SCROLL_SPEED
        );
        const tween = scrollTo(Math.min(ziel.textCenter, this.getMaxScroll()), {
            context: this.getScrollContext(),
            duration: dauer,
            easing: schritte > 1 ? gleichmaessig(dauer) : Easings.quadInOut,
        });
        if (!tween) return;
        this.knopfZielIndex = index;
        this.cancelScroll = tween.cancel;

        const absicht = radAbsicht(performance.now());
        const rad = (e: WheelEvent) => {
            if (absicht(performance.now(), e.deltaY)) tween.cancel();
        };
        const abbrechen = () => tween.cancel();
        this.viewerFrame.addEventListener("wheel", rad, { passive: true });
        this.viewerFrame.addEventListener("touchstart", abbrechen, { passive: true });
        this.viewerFrame.addEventListener("mousedown", abbrechen);
        window.addEventListener("keydown", abbrechen);
        // Abbruch settelt die Promise ebenfalls (scrollTo) — hier wird immer aufgeräumt
        void tween.promise.finally(() => {
            this.viewerFrame.removeEventListener("wheel", rad);
            this.viewerFrame.removeEventListener("touchstart", abbrechen);
            this.viewerFrame.removeEventListener("mousedown", abbrechen);
            window.removeEventListener("keydown", abbrechen);
            if (this.knopfZielIndex === index) {
                this.knopfZielIndex = null;
            }
        });
    }

    get slideCount() {
        return this.slidePositions.length;
    }

    get beforeStory() {
        if (this.slideCount === 0) {
            return true;
        }
        return this.scrollTop < this.innerHeight;
    }

    // Rein positionsbasiert wie in Jans 1.0 — KEIN Einweg-Latch mehr (Jans
    // Fund 30.08.2026: die Nav-Knöpfe blenden am Story-Ende aus und kamen
    // beim Zurückscrollen NIE wieder, weil afterStoryLatched für immer true
    // blieb). Der einzige historische Latch-Konsument war der spacer-Toggle
    // im Player, der seit 28.05.2026 konstant ist (s. Kommentar dort).
    get afterStory() {
        if (this.slideCount === 0) {
            return false;
        }

        const lastSlide = this.slidePositions.at(-1)!;
        return lastSlide.top + lastSlide.height <= this.scrollTop;
    }
}

export default VisualStoryStore;
